import React, { type ReactElement, useState, type ReactNode, useEffect } from 'react'
import { useTheme } from '@mui/material'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import { type CustomTab } from 'common/utils/types'

import styles from './CustomTabs.module.scss'

interface TabPanelProps {
  children?: ReactNode
  index: number
  activeTabIndex: number
}

const TabPanel = (props: TabPanelProps): ReactElement => {
  const { children, activeTabIndex, index, ...other } = props
  return (
    <div
      role="tabpanel"
      hidden={activeTabIndex !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {activeTabIndex === index && <div>{children}</div>}
    </div>
  )
}

const accesibilityProps = (index: number): object => {
  return {
    id: `tab-${index}`,
    'aria-controls': `tabpanel-${index}`,
  }
}

interface CustomTabsProps {
  tabs: CustomTab[]
  tabOpened?: number
}
const CustomTabs: React.FC<CustomTabsProps> = ({ tabs, tabOpened }): ReactElement => {
  const { palette, typography } = useTheme()
  const [tabIndex, setTabIndex] = useState<number>(() => {
    const savedTabIndex = localStorage.getItem('selectedTabIndex')
    return savedTabIndex ? parseInt(savedTabIndex, 10) : 0
  })
  const handleChange = (event: any, newTabIndex: number): void => {
    setTabIndex(newTabIndex)
  }

  useEffect(() => {
    localStorage.setItem('selectedTabIndex', String(tabIndex))
  }, [tabIndex])

  return (
    <div className={styles.container}>
      <div className={styles.tabsHeader}>
        <Tabs
          value={tabOpened ?? tabIndex}
          onChange={handleChange}
          className={styles.tabs}
          sx={{
            minHeight: '54px',
            '& .MuiTabs-indicator': {
              backgroundColor: 'transparent',
            },
          }}
        >
          {tabs.map((tab: CustomTab, index: number) => (
            <Tab
              label={tab.title}
              key={index}
              {...accesibilityProps(index)}
              sx={{
                paddingTop: 0,
                paddingBottom: 0,
                minHeight: '54px',
                textTransform: 'none',
                ...typography.title1,
                '&.MuiTab-root': {
                  display: 'flex',
                },
                '&.Mui-selected': {
                  color: palette.white.main,
                  backgroundColor: palette.blue600.main,
                  borderRadius: '10px 0 0 0',
                  borderBottom: 'none',
                },
                backgroundColor: palette.blue800.main,
                borderRadius: '0 0 10px 10px',
              }}
            />
          ))}
        </Tabs>
      </div>
      {tabs.map((tab: CustomTab, index: number) => (
        <TabPanel activeTabIndex={tabOpened ?? tabIndex} index={index} key={index}>
          {tab.content}
        </TabPanel>
      ))}
    </div>
  )
}

export default CustomTabs
